// @ts-nocheck
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { suppliersApi } from '@/services/api';
import { Supplier } from '@/types';
import { useAuth } from '@/hooks/useAuth';
import { Check } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer } from 'recharts';
import { formatDate } from '@/utils/dateFormat';

// CR-19: chart definitions for the Suppliers chart view
const CHART_CONFIGS: Record<string, any> = {
  outcomes: {
    label: 'Outcomes',
    title: 'Respondent Outcomes by Supplier',
    subtitle: 'ResearchDefender accepted vs. failed respondents across all projects',
    bars: [
      { key: 'rdAccepts', name: 'RD Accept', fill: '#16A34A' },
      { key: 'rdFails', name: 'RD Fail', fill: '#EF4444' },
    ],
  },
  successRate: {
    label: 'Success rate',
    title: 'Success Rate by Supplier',
    subtitle: 'RD pass rate — accepted / (accepted + failed)',
    unit: '%',
    // CR-19: higher success rate is better → most successful at the top
    sort: { key: 'successRate', dir: 'desc' },
    topLabel: 'Most successful at the top',
    bars: [{ key: 'successRate', name: 'Success rate', fill: '#18A5F9' }],
  },
  threat: {
    label: 'Threat score',
    title: 'Average Threat Score by Supplier',
    subtitle: 'RD threat score — overall vs completes only (lower is better)',
    // CR-19: lower threat is better → best performer at the top
    sort: { key: 'avgThreatOverall', dir: 'asc' },
    topLabel: 'Best performing at the top',
    bars: [
      { key: 'avgThreatOverall', name: 'Overall', fill: '#18A5F9' },
      { key: 'avgThreatSuccesses', name: 'Completes only', fill: '#16A34A' },
    ],
  },
  geoFail: {
    label: 'Geo-mismatch fails',
    title: 'Geo-Mismatch Fail Rate by Supplier',
    subtitle: 'Share of respondents failed for country mismatch',
    unit: '%',
    // CR-19: lower fail rate is better → best performer at the top
    sort: { key: 'geoFailPct', dir: 'asc' },
    topLabel: 'Best performing at the top',
    bars: [{ key: 'geoFailPct', name: 'Geo-mismatch', fill: '#F59E0B' }],
  },
  dupFail: {
    label: 'Duplicate fails',
    title: 'Duplicate Fail Rate by Supplier',
    subtitle: 'Share of respondents failed as potential duplicates',
    unit: '%',
    // CR-19: lower fail rate is better → best performer at the top
    sort: { key: 'dupFailPct', dir: 'asc' },
    topLabel: 'Best performing at the top',
    bars: [{ key: 'dupFailPct', name: 'Duplicate', fill: '#A855F7' }],
  },
  status: {
    label: 'Status breakdown',
    title: 'Status Breakdown by Supplier',
    subtitle: 'Respondents split by final status across all projects',
    bars: [
      { key: 'completed', name: 'Completed', fill: '#16A34A' },
      { key: 'terminated', name: 'Terminated', fill: '#F59E0B' },
      { key: 'quotaFull', name: 'Quota Full', fill: '#6366F1' },
      { key: 'securityTerminated', name: 'Security Terminated', fill: '#EF4444' },
    ],
  },
};

const SuppliersPage = () => {
  const [error, setError] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [apiFilter, setApiFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<'cards' | 'chart'>('cards');

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const { data: response, isLoading, error: queryError } = useQuery(
    ['suppliers', currentPage, searchTerm, apiFilter],
    () => suppliersApi.getAll({
      page: currentPage,
      limit: 20,
      search: searchTerm || undefined,
    }),
    {
      onError: (error) => {
        setError('Failed to load suppliers');
        console.error('Suppliers query error:', error);
      },
    }
  );

  const deleteMutation = useMutation(suppliersApi.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('suppliers');
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || 'Failed to delete supplier');
    },
  });

  const suppliers = response?.data || [];
  const pagination = response?.pagination;

  // CR-19: supplier-level tracking metrics (all suppliers)
  const { data: metricsResponse } = useQuery(
    'supplier-metrics-all',
    () => suppliersApi.getAllMetrics(),
    { staleTime: 30000 }
  );

  const metricsList = metricsResponse?.data || [];

  const chartData = metricsList
    .filter((m: any) => m.total > 0)
    .map((m: any) => ({
      name: m.name,
      successes: m.successes,
      fails: m.fails,
      rdAccepts: m.rdAccepts,
      rdFails: m.rdFails,
      completed: m.completed,
      terminated: m.terminated,
      quotaFull: m.quotaFull,
      securityTerminated: m.securityTerminated,
      successRate: m.successRate,
      avgThreatOverall: m.avgThreatOverall ?? 0,
      avgThreatSuccesses: m.avgThreatSuccesses ?? 0,
      geoFailPct: m.geoFailPct,
      dupFailPct: m.dupFailPct,
    }));

  const handleDeleteSupplier = (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      deleteMutation.mutate(id);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <div className="h-9 w-48 md-skeleton mb-2"></div>
            <div className="h-5 w-32 md-skeleton"></div>
          </div>
          <div className="h-11 w-36 md-skeleton rounded-[10px]"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-[14px] border border-[#ECEEF1] p-6 space-y-4">
              <div className="h-6 md-skeleton w-3/4"></div>
              <div className="space-y-2">
                <div className="h-4 md-skeleton w-full"></div>
                <div className="h-4 md-skeleton w-5/6"></div>
                <div className="h-4 md-skeleton w-2/3"></div>
              </div>
              <div className="flex gap-2 pt-4">
                <div className="h-10 md-skeleton flex-1 rounded-[10px]"></div>
                <div className="h-10 md-skeleton w-20 rounded-[10px]"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error || queryError) {
    return (
      <div className="text-center py-20">
        <div className="text-[#EF4444] text-lg mb-4 font-medium">
          {error || 'Failed to load suppliers. Please try again.'}
        </div>
        <button
          onClick={() => window.location.reload()}
          className="md-btn md-btn-primary"
        >
          Refresh
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-end animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold font-heading text-[#1A1F2B] mb-1">Suppliers</h1>
          <p className="text-[#6B7280] text-[15px]">
            {suppliers.length} {suppliers.length === 1 ? 'supplier' : 'suppliers'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* CR-19: View toggle */}
          <div className="flex rounded-[10px] border border-[#ECEEF1] bg-white p-1">
            <button
              onClick={() => setViewMode('cards')}
              className={`px-3 py-1.5 rounded-[7px] text-[13px] font-semibold transition-colors ${
                viewMode === 'cards' ? 'bg-[#18A5F9] text-white' : 'text-[#6B7280] hover:text-[#1A1F2B]'
              }`}
            >
              Cards
            </button>
            <button
              onClick={() => setViewMode('chart')}
              className={`px-3 py-1.5 rounded-[7px] text-[13px] font-semibold transition-colors ${
                viewMode === 'chart' ? 'bg-[#18A5F9] text-white' : 'text-[#6B7280] hover:text-[#1A1F2B]'
              }`}
            >
              Charts
            </button>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="md-btn md-btn-primary"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Add Supplier
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="text-red-700 text-sm bg-red-50 p-3 border border-red-200 rounded-xl">{error}</div>
      )}

      {/* CR-19: Supplier Performance Charts */}
      {viewMode === 'chart' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {Object.entries(CHART_CONFIGS).map(([key, cfg]: [string, any]) => {
            const isPercent = cfg.unit === '%';
            // CR-19: sort per-chart so the best/worst performer sits at the top
            const data = cfg.sort
              ? [...chartData].sort((a: any, b: any) =>
                  cfg.sort.dir === 'asc'
                    ? (a[cfg.sort.key] ?? 0) - (b[cfg.sort.key] ?? 0)
                    : (b[cfg.sort.key] ?? 0) - (a[cfg.sort.key] ?? 0)
                )
              : chartData;
            return (
              <div key={key} className="md-card p-6 animate-fade-in-up">
                <h2 className="text-lg font-bold font-heading text-[#1A1F2B] mb-1">{cfg.title}</h2>
                <p className="text-[13px] text-[#9CA3AF] mb-1">{cfg.subtitle}</p>
                {cfg.topLabel ? (
                  <p className="text-[12px] font-semibold text-[#6B7280] mb-4 flex items-center gap-1">
                    <span aria-hidden>↑</span> {cfg.topLabel}
                  </p>
                ) : (
                  <div className="mb-5" />
                )}
                {data.length > 0 ? (
                  <ResponsiveContainer width="100%" height={Math.max(240, data.length * 52)}>
                    <BarChart data={data} layout="vertical" margin={{ top: 4, right: 24, bottom: 4, left: 24 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#ECEEF1" horizontal={false} />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 12, fill: '#6B7280' }}
                        allowDecimals={false}
                        domain={[0, 'auto']}
                        tickFormatter={isPercent ? (v: number) => `${v}%` : undefined}
                      />
                      <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12, fill: '#374151' }} />
                      <Tooltip cursor={{ fill: '#F5F7FA' }} formatter={isPercent ? (v: any) => `${v}%` : undefined} />
                      <Legend wrapperStyle={{ fontSize: 13 }} />
                      {cfg.bars.map((b: any, i: number) => (
                        <Bar
                          key={b.key}
                          dataKey={b.key}
                          name={b.name}
                          stackId={b.stackId}
                          fill={b.fill}
                          radius={i === cfg.bars.length - 1 ? [0, 4, 4, 0] : [0, 0, 0, 0]}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-[#9CA3AF] py-8 text-center">No respondent data available yet.</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {viewMode === 'cards' && (suppliers.length === 0 ? (
        <div className="text-center py-20 animate-fade-in-up stagger-2">
          <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-[#E8F4FD] flex items-center justify-center">
            <svg className="w-8 h-8 text-[#18A5F9]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
            </svg>
          </div>
          <p className="text-lg text-[#6B7280] mb-6">
            No suppliers yet. Add your first one to get started.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="md-btn md-btn-primary"
          >
            Create Your First Supplier
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {suppliers.map((supplier: Supplier, index: number) => {
            const primaryContact = supplier.contacts?.find(c => c.isPrimary);
            const urlCount = [
              supplier.completeUrlTemplate,
              supplier.terminateUrlTemplate,
              supplier.quotaFullUrlTemplate,
              supplier.securityTerminateUrlTemplate
            ].filter(Boolean).length;

            return (
              <div
                key={supplier.id}
                className={`md-card p-6 cursor-pointer animate-fade-in-up stagger-${Math.min(index + 1, 6)}`}
                onClick={() => navigate(`/admin/suppliers/${supplier.id}`)}
              >
                {/* Card Header */}
                <div className="mb-4">
                  <h3 className="text-lg font-bold font-heading gradient-text mb-1">
                    {supplier.name}
                  </h3>
                  <p className="text-[13px] text-[#9CA3AF] font-mono break-all">
                    {supplier.id}
                  </p>
                </div>

                {/* Card Content */}
                <div className="space-y-2.5 mb-5 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-[#9CA3AF]">Primary Contact</span>
                    <span className="font-semibold text-[#374151] truncate ml-2 max-w-[160px]">
                      {primaryContact?.name || 'None'}
                    </span>
                  </div>

                  {primaryContact?.email && (
                    <div className="flex items-center justify-between">
                      <span className="text-[#9CA3AF]">Email</span>
                      <span className="font-semibold text-[#374151] truncate ml-2 max-w-[160px]">
                        {primaryContact.email}
                      </span>
                    </div>
                  )}

                  {urlCount > 0 && (
                    <div className="flex items-center gap-1.5 text-[#18A5F9] text-xs font-semibold">
                      <Check className="w-3.5 h-3.5" />
                      {urlCount} redirect URL{urlCount !== 1 ? 's' : ''} configured
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <span className="text-[#9CA3AF]">Created</span>
                    <span className="font-semibold text-[#374151]">
                      {formatDate(supplier.createdAt)}
                    </span>
                  </div>
                </div>

                {/* Card Actions */}
                <div className="flex gap-2 pt-4 border-t border-[#ECEEF1]" onClick={(e) => e.stopPropagation()}>
                  <Link
                    to={`/admin/suppliers/${supplier.id}`}
                    className="md-btn md-btn-outlined flex-1 text-center justify-center text-[13px]"
                  >
                    View
                  </Link>
                  {isSuperAdmin && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSupplier(supplier.id, supplier.name);
                      }}
                      className="md-btn md-btn-danger text-[13px] px-4"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {/* Pagination */}
      {viewMode === 'cards' && pagination && pagination.totalPages > 1 && (
        <div className="flex justify-center items-center gap-3 mt-8 animate-fade-in-up stagger-6">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="md-btn md-btn-outlined px-4 py-2 disabled:opacity-40 disabled:cursor-not-allowed text-[13px]"
          >
            Previous
          </button>
          <span className="text-sm text-[#6B7280] font-medium">
            Page {currentPage} of {pagination.totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(pagination.totalPages, p + 1))}
            disabled={currentPage === pagination.totalPages}
            className="md-btn md-btn-outlined px-4 py-2 disabled:opacity-40 disabled:cursor-not-allowed text-[13px]"
          >
            Next
          </button>
        </div>
      )}

      {/* Create Supplier Modal */}
      {showCreateModal && (
        <CreateSupplierModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            queryClient.invalidateQueries('suppliers');
          }}
        />
      )}
    </div>
  );
};

// Create Supplier Modal Component
const CreateSupplierModal = ({
  onClose,
  onSuccess
}: {
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const [formData, setFormData] = useState({
    name: '',
    placeholderToken: '',
    completeUrlTemplate: '',
    terminateUrlTemplate: '',
    quotaFullUrlTemplate: '',
    securityTerminateUrlTemplate: '',
  });
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await suppliersApi.create(formData);
      if (result.success) {
        onSuccess();
      } else {
        setError(result.error || 'Failed to create supplier');
      }
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to create supplier');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-[0_24px_48px_rgba(0,0,0,0.12)] border border-[#ECEEF1]">
        <h2 className="text-2xl font-bold font-heading gradient-text mb-6">Add New Supplier</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Supplier Name *</label>
              <div className="md-outlined-textfield">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full"
                  required
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Placeholder Token</label>
              <div className="md-outlined-textfield">
                <input
                  type="text"
                  name="placeholderToken"
                  value={formData.placeholderToken}
                  onChange={handleChange}
                  className="w-full"
                  placeholder="e.g. [%RID%]"
                />
              </div>
            </div>
          </div>

          <div className="space-y-4 pt-2">
            <h3 className="text-[13px] font-bold text-[#374151] uppercase tracking-wider">Redirect URLs</h3>
            {[
              { name: 'completeUrlTemplate', label: 'Complete URL' },
              { name: 'terminateUrlTemplate', label: 'Terminate URL' },
              { name: 'quotaFullUrlTemplate', label: 'Quota Full URL' },
              { name: 'securityTerminateUrlTemplate', label: 'Security Terminate URL' },
            ].map(({ name, label }) => (
              <div key={name}>
                <label className="block text-sm font-medium text-[#6B7280] mb-1.5">{label}</label>
                <div className="md-outlined-textfield">
                  <input
                    type="url"
                    name={name}
                    value={(formData as any)[name]}
                    onChange={handleChange}
                    className="w-full"
                    placeholder={`https://example.com/${name.replace('UrlTemplate', '')}?pid=[PANEL_ID]`}
                  />
                </div>
              </div>
            ))}
          </div>

          {error && (
            <div className="text-red-700 text-sm bg-red-50 p-3 border border-red-200 rounded-xl">{error}</div>
          )}

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="md-btn md-btn-outlined flex-1">
              Cancel
            </button>
            <button type="submit" disabled={isLoading} className="md-btn md-btn-primary flex-1">
              {isLoading ? 'Creating...' : 'Create Supplier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SuppliersPage;
